import { COUNTRIES, type CountryCode } from './country';

const AU_DOMAIN = 'buywisely.com.au';

/**
 * The same-host path a page is SERVED at: always country-prefixed
 * (/au/product/slug, /us/category/X). The middleware passes /<country>/...
 * straight through on every host (buywisely.com.au, buywisely.io, local), so a
 * link or redirect built with it reaches the page without a further redirect.
 * The unprefixed form (/product/slug) costs a 301 on buywisely.com.au.
 */
export function servedPath(country: CountryCode, path: string = ''): string {
  return `/${country}${path}`;
}

/**
 * Internal link path. Same as servedPath: country-prefixed on every host.
 * (It used to drop the prefix on buywisely.com.au, which made every internal
 * click a 301 → /au/...; it also differed between server render, where
 * APP_BASE_URL is set, and the browser, where it is not.)
 */
export function countryPath(country: CountryCode, path: string = ''): string {
  return servedPath(country, path);
}

/**
 * Full absolute URL for a country (structured data, hreflang, etc.)
 * AU: https://buywisely.com.au/au/product/slug
 * US: https://buywisely.io/us/product/slug
 */
export function countryAbsoluteUrl(country: CountryCode, path: string = ''): string {
  return canonicalUrl(country, path);
}

/**
 * Canonical / indexable absolute URL — ALWAYS country-prefixed so it matches the
 * URL that actually serves HTTP 200. The legacy AU form (/category/X) 301-redirects
 * to /au/category/X; a rel=canonical (or hreflang / JSON-LD url) pointing at a
 * redirecting URL is self-conflicting and Google follows the redirect instead,
 * suppressing indexation. Use this for canonical, OpenGraph url, hreflang targets,
 * and JSON-LD item/breadcrumb URLs.
 */
export function canonicalUrl(country: CountryCode, path: string = ''): string {
  const domain = COUNTRIES[country].domain;
  return `https://${domain}/${country}${path || ''}`;
}

/**
 * For cross-domain country switching (CountrySelector).
 * Returns the served path if the target country lives on this domain, or the
 * full served URL on the other domain — country-prefixed either way, so the
 * switch lands without a further redirect.
 */
export function countryUrl(country: CountryCode, path: string = ''): string {
  const baseUrl = process.env.BASE_URL || process.env.APP_BASE_URL || '';
  const targetDomain = COUNTRIES[country].domain;

  // Local dev — just use country prefix
  if (baseUrl.includes('localhost')) {
    return servedPath(country, path);
  }

  const currentIsTarget =
    targetDomain === AU_DOMAIN ? baseUrl.includes(AU_DOMAIN) : baseUrl.includes(targetDomain);
  if (currentIsTarget) return servedPath(country, path);
  return `https://${targetDomain}${servedPath(country, path)}`;
}
